/**
 * A hook to provide access to category data from multiple sources.
 *
 * > **💡 NOTE**: This hook internally avails of [[useHome]] and [[useCategory]].
 *
 * - If a `categoryId` is not provided:
 *     - Where `categoryId` is not provided, this hook looks for a `categoryFragment` in the context and returns it as an `ICategory` object.
 *     - If no `categoryFragment` is found, it will try to obtain a `Category` from `useCategory` hook and return as an `ICategory` object.
 *     - Finally, if none of `categoryId`, `categoryFragment` or `Category` outlined above are found, it will simply return `undefined`.
 * - If a `categoryId` is provided:
 *     - If provided, the hook will first check for a `categoryFragment` and see if the `categoryId` matches it. If matched, it will return the `categoryFragment` as an `ICategory` object.
 *     - If no `categoryFragment` is found, it will try to obtain a `Category` from `useCategory` hook and return it as an `ICategory` object.
 *     - If no `Category`  is found, it will look for a `home` value from `useHome` hook. If `home` contains `categories` one of which matches the provided `categoryId`, it will return a matching `ICategory` object.
 *     - Finally, if none of `categoryId`, `categoryFragment`, `Category` or `home` outlined above are found, it will simply return `undefined`.
 *
 * @module
 */
import { useContext, useMemo } from 'react';
import { CategoryContext } from '../contexts';
import useCategory from './useCategory';
import useHome from './useHome';
/**
 *
 * # useCategoryFragment
 *
 * Usage example:
 *
 * ```javascript
 * import useCategoryFragment from "@elevio/kb-kit/lib/hooks/useCategoryFragment";
 *
 * const MyCategoryTitle = ({ categoryId }) => {
 *   const category = useCategoryFragment(categoryId);
 *   console.log(category); // ICategory | undefined
 *
 *   return !category ? null : <div>{category.title}</div>;
 * };
 * ```
 */
export default function useCategoryFragment(categoryId) {
    const categoryFragment = useContext(CategoryContext);
    const category = useCategory();
    const home = useHome();
    const value = useMemo(() => {
        if (!categoryId) {
            if (categoryFragment)
                return categoryFragment;
            if (category)
                return {
                    id: category.id,
                    title: category.title,
                    slug: category.slug,
                    featuredImageUrl: category.featuredImageUrl,
                    articles: category.articles,
                };
            return undefined;
        }
        if (categoryFragment && categoryFragment.id === categoryId) {
            return categoryFragment;
        }
        if (category) {
            if (category.id === categoryId) {
                return {
                    id: category.id,
                    title: category.title,
                    slug: category.slug,
                    featuredImageUrl: category.featuredImageUrl,
                    articles: category.articles,
                };
            }
            const sub = (category.subCategories || []).find((_sub) => _sub.id === categoryId);
            if (sub) {
                return sub;
            }
        }
        if (home && home.categories) {
            const _category = home.categories.find((c) => c.id === categoryId);
            if (_category) {
                return {
                    id: _category.id,
                    title: _category.title,
                    slug: _category.slug,
                    featuredImageUrl: _category.featuredImageUrl,
                    articles: _category.articles,
                };
            }
        }
        return undefined;
    }, [categoryId, categoryFragment, category, home]);
    return value;
}
//# sourceMappingURL=useCategoryFragment.js.map